import { useState, useEffect } from 'react';
import { Seo, usePageSeo, assetUrl, absUrl } from '../lib/seo';
import { PageSeoContent } from '../components/common/RichContent';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Phone, ChevronRight, Shield, Users, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { DoctorCard } from '../components/common/DoctorCard';
import { LogoLoader } from '../components/common/LogoLoader';
import { doctorsAPI, locationsAPI } from '../lib/api';
import { getWhatsAppLink } from '../lib/utils';

export const CityPage = () => {
  const seo = usePageSeo();
  const { slug } = useParams();
  const [location, setLocation] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const locationsRes = await locationsAPI.getAll();
        const foundLocation = locationsRes.data.find(l => l.slug === slug);
        setLocation(foundLocation);

        if (foundLocation) {
          const doctorsRes = await doctorsAPI.getAll({ doctor_status: 'approved', location_id: foundLocation.id });
          setDoctors(doctorsRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-alabaster">
        <LogoLoader className="min-h-[70vh]" />
      </div>
    );
  }

  if (!location) {
    return (
      <div className="min-h-screen bg-alabaster flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold text-gray-700 mb-4">City not found</h2>
          <Link to="/doctors">
            <Button>Browse All Doctors</Button>
          </Link>
        </div>
      </div>
    );
  }

  const faqs = [
    {
      question: `How do I find the best dermatologist in ${location.city}?`,
      answer: `DrPortals curates verified dermatologists in ${location.city} based on qualifications, experience, and patient reviews. Simply browse our listings or contact us via WhatsApp for personalized recommendations.`
    },
    {
      question: `What treatments are available in ${location.city}?`,
      answer: `Our dermatologists in ${location.city} offer a wide range of treatments including acne treatment, hair loss solutions, skin rejuvenation, weight management, and aesthetic procedures like Botox and fillers.`
    },
    {
      question: `How do I book an appointment?`,
      answer: `You can easily book an appointment by clicking the WhatsApp button on any doctor's profile. Our team will help you schedule a convenient time with your chosen dermatologist.`
    }
  ];

  return (
    <div className="min-h-screen bg-alabaster" data-testid={`city-page-${slug}`}>
      <Seo override={seo} title={`Best Dermatologists in ${location.city} | Verified Skin Specialists | DrPortals`}
        description={`Find verified dermatologists, skin specialists and hair experts in ${location.city}, ${location.country}. Compare doctors and book on WhatsApp.`}
        jsonLd={{ "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": absUrl('/') },
          { "@type": "ListItem", "position": 2, "name": `Dermatologists in ${location.city}`, "item": absUrl(`/city/${slug}`) }] }} />
      {/* Hero */}
      <section className="bg-navy-800 text-white py-16 md:py-20 px-4 grain-overlay">
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-gold mb-4">
            <MapPin className="w-5 h-5" />
            <span>{location.country}</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            {seo.h1 || `Best Dermatologists in ${location.city}`}
          </h1>
          <p className="text-gray-300 max-w-2xl mb-8">
            Find verified skin specialists, hair experts, and aesthetic practitioners in {location.city}. Quality care, trusted professionals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href={getWhatsAppLink(`Hi, I'm looking for a dermatologist in ${location.city}.`)} target="_blank" rel="noopener noreferrer">
              <Button className="rounded-full bg-whatsapp hover:bg-whatsapp/90 text-white px-8 py-6 gap-2 w-full sm:w-auto">
                <Phone className="w-5 h-5" />
                Get Help Finding a Doctor
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-6 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            <span className="flex items-center gap-2 text-gray-600">
              <Shield className="w-5 h-5 text-gold" />
              Verified Doctors
            </span>
            <span className="flex items-center gap-2 text-gray-600">
              <Users className="w-5 h-5 text-gold" />
              {doctors.length}+ Specialists
            </span>
            <span className="flex items-center gap-2 text-gray-600">
              <Star className="w-5 h-5 text-gold" />
              Top Rated
            </span>
          </div>
        </div>
      </section>

      {/* Doctors Grid */}
      <section className="py-16 md:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy-900">
              Dermatologists in {location.city}
            </h2>
            <Link to="/doctors">
              <Button variant="outline" className="rounded-full border-navy-800 text-navy-800">
                View All Doctors
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          {doctors.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {doctors.map((doctor) => (
                <DoctorCard key={doctor.id} doctor={doctor} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl">
              <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-gray-700 mb-2">
                No doctors listed in {location.city} yet
              </h3>
              <p className="text-gray-500 mb-6">
                We're expanding our network. Contact us to find a specialist near you.
              </p>
              <a href={getWhatsAppLink(`Hi, I'm looking for a dermatologist in ${location.city}.`)} target="_blank" rel="noopener noreferrer">
                <Button className="rounded-full bg-whatsapp hover:bg-whatsapp/90 text-white">
                  Get Help via WhatsApp
                </Button>
              </a>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-navy-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`faq-${index}`} className="bg-gray-50 rounded-xl mb-3 border-0 px-6">
                <AccordionTrigger className="text-left font-medium text-navy-900 hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 px-4 bg-navy-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Find the Best Dermatologist in {location.city}
          </h2>
          <p className="text-gray-300 mb-8 max-w-xl mx-auto">
            Our team will help you connect with verified specialists in {location.city} based on your specific needs.
          </p>
          <a href={getWhatsAppLink(`Hi, I need help finding a dermatologist in ${location.city}.`)} target="_blank" rel="noopener noreferrer">
            <Button className="rounded-full bg-whatsapp hover:bg-whatsapp/90 text-white px-10 py-6 text-lg gap-2">
              <Phone className="w-5 h-5" />
              Chat on WhatsApp
            </Button>
          </a>
        </div>
      </section>

      {/* Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": `DrPortals - Dermatologists in ${location.city}`,
          "description": `Find verified dermatologists and skin specialists in ${location.city}`,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": location.city,
            "addressCountry": location.country
          }
        })
      }} />
      <PageSeoContent seo={seo} />
    </div>
  );
};
